/**
 * Locale provider + `useT()` hook.
 *
 * Source order of locale resolution:
 *  1. Stored override in localStorage (user explicitly changed it).
 *  2. `me.locale` from the server (synced via PATCH /me).
 *  3. Telegram WebApp's `language_code`.
 *  4. Browser `navigator.language`.
 *  5. 'en'.
 *
 * Changing the language calls PATCH /me so it persists across devices.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { SUPPORTED_LOCALES, type SupportedLocale } from '@waylo/shared';
import { DICTIONARY, LOCALE_LABELS, type StringKey } from './i18n-strings';
import { getWebApp } from './telegram';
import { updateMe } from './api';

interface I18nCtx {
  locale: SupportedLocale;
  t: (key: StringKey, vars?: Record<string, string | number>) => string;
  setLocale: (locale: SupportedLocale, persist?: boolean) => Promise<void>;
}

const Ctx = createContext<I18nCtx | null>(null);

const LS_KEY = 'waylo_locale';

function detectInitial(serverLocale?: SupportedLocale): SupportedLocale {
  try {
    const ls = localStorage.getItem(LS_KEY);
    if (ls && (SUPPORTED_LOCALES as readonly string[]).includes(ls)) {
      return ls as SupportedLocale;
    }
  } catch {
    /* ignore */
  }
  if (serverLocale) return serverLocale;
  const tg = getWebApp();
  const tgLang = tg?.initDataUnsafe?.user?.language_code;
  if (tgLang) {
    const tag = tgLang.toLowerCase().split(/[-_]/)[0] ?? '';
    if ((SUPPORTED_LOCALES as readonly string[]).includes(tag)) return tag as SupportedLocale;
  }
  const nav = (navigator.language ?? 'en').toLowerCase().split(/[-_]/)[0] ?? 'en';
  if ((SUPPORTED_LOCALES as readonly string[]).includes(nav)) return nav as SupportedLocale;
  return 'en';
}

interface ProviderProps {
  children: ReactNode;
  /** Server-known locale, if available. Comes from useSession().me?.locale. */
  serverLocale?: SupportedLocale;
}

export function I18nProvider({ children, serverLocale }: ProviderProps): JSX.Element {
  const [locale, setLocaleState] = useState<SupportedLocale>(() => detectInitial(serverLocale));

  // If the server-known locale changes (e.g. after PATCH /me) and the user hasn't
  // explicitly overridden, sync.
  useEffect(() => {
    if (!serverLocale) return;
    try {
      const ls = localStorage.getItem(LS_KEY);
      if (!ls && serverLocale !== locale) setLocaleState(serverLocale);
    } catch {
      /* ignore */
    }
  }, [serverLocale, locale]);

  const setLocale = useCallback(async (next: SupportedLocale, persist = true): Promise<void> => {
    setLocaleState(next);
    try {
      localStorage.setItem(LS_KEY, next);
    } catch {
      /* ignore */
    }
    if (persist) {
      try {
        await updateMe({ locale: next });
      } catch {
        // non-fatal — local-only change still works for this session
      }
    }
  }, []);

  const t = useCallback(
    (key: StringKey, vars?: Record<string, string | number>): string => {
      const dict = DICTIONARY[locale] as Record<string, string>;
      let str = dict[key] ?? (DICTIONARY.en as Record<string, string>)[key] ?? key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          str = str.replaceAll(`{${k}}`, String(v));
        }
      }
      return str;
    },
    [locale],
  );

  const value = useMemo<I18nCtx>(() => ({ locale, t, setLocale }), [locale, t, setLocale]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useT(): I18nCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useT must be used inside <I18nProvider>');
  return v;
}

/** Pluralization helper — minimal, English/Slavic-ish. */
export function plural(n: number, singular: string, multiple: string): string {
  return n === 1 ? singular : multiple;
}

/**
 * Maps a server ApiError to a localized string. The error code (e.g. 'ANTI_ABUSE_REJECT')
 * is looked up under `err.<CODE>`; falls back to the raw server message if no
 * translation exists.
 */
export function translateError(
  t: (k: StringKey, vars?: Record<string, string | number>) => string,
  err: unknown,
): string {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code: string }).code);
    const key = `err.${code}` as StringKey;
    const translated = t(key);
    // If translation === key, it means no translation found; use server message.
    if (translated !== key) return translated;
    if ('message' in err && typeof (err as { message?: unknown }).message === 'string') {
      return (err as { message: string }).message;
    }
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * Language-gated place scope: the UI language picks which country's places you
 * can choose. English = all countries (international). sr→Serbia, hr→Croatia,
 * bs→Bosnia. The other 6 Balkan countries are reachable only in English mode.
 */
export function localeCountry(loc: SupportedLocale): 'RS' | 'HR' | 'BA' | 'all' {
  return loc === 'sr' ? 'RS' : loc === 'hr' ? 'HR' : loc === 'bs' ? 'BA' : 'all';
}

/** Maps a BCP-47-ish locale to JS Date locale tag for formatting day/time. */
export function jsLocale(loc: SupportedLocale): string {
  return loc === 'en' ? 'en-US' : loc === 'sr' ? 'sr-Latn-RS' : loc === 'bs' ? 'bs-Latn-BA' : 'hr-HR';
}

export { LOCALE_LABELS, SUPPORTED_LOCALES };
export type { SupportedLocale };
